"use client"

import { ReactNode, useId } from "react"
import { usePopupStore } from "../store/usePopupStore"

export const PopupBaseLayout = ({
  title,
  children,
  buttons,
  className,
  ariaLabelledBy,
  ariaDescribedBy,
}: {
  title?: ReactNode
  children: ReactNode
  buttons: ReactNode
  className?: string
  ariaLabelledBy?: string
  ariaDescribedBy?: string
}) => {
  return (
    <div
      className={`${className && className} ${title ? "pt-5" : "pt-8"} flex max-h-[90dvh] w-[500px] max-w-[90vw] flex-col rounded-lg bg-white px-5 pb-4 sm:min-w-[320px]`}
      aria-labelledby={ariaLabelledBy}
      aria-describedby={ariaDescribedBy}
    >
      {title && (
        <div
          id={ariaLabelledBy}
          tabIndex={0}
          data-popup-focus="title"
          className="mb-6 flex justify-center rounded focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-500"
        >
          {title}
        </div>
      )}
      <div
        id={ariaDescribedBy}
        tabIndex={0}
        data-popup-focus="content"
        className="flex min-h-0 flex-1 flex-col items-center rounded focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-500"
      >
        {children}
      </div>
      <div className="mt-6 flex flex-row">{buttons}</div>
    </div>
  )
}

export const PopupLayout = ({
  title,
  children,
  buttons,
  className,
}: {
  title?: string
  children: ReactNode
  buttons: {
    id?: string
    title: string
    disabled?: boolean
    onClick?: () => void
  }[]
  className?: string
}) => {
  const { closePopup } = usePopupStore()
  const titleId = useId()
  const contentId = useId()

  return (
    <PopupBaseLayout
      ariaLabelledBy={title ? titleId : undefined}
      ariaDescribedBy={contentId}
      title={
        title && (
          <span className="typo-rl-headline1 font-bold text-rl-gray-900">
            {title}
          </span>
        )
      }
      buttons={
        buttons.length == 1 ? (
          <button
            id={buttons[0].id}
            className={`${buttons[0]?.disabled ? "bg-rl-primary-disable text-rl-gray-font-caption" : "bg-rl-primary-normal text-rl-common-white"} typo-rl-body2 h-12 flex-1 rounded-lg font-medium`}
            disabled={buttons[0].disabled}
            onClick={buttons[0].onClick}
          >
            {buttons[0].title}
          </button>
        ) : buttons.length == 2 ? (
          <>
            <button
              id={buttons[0].id}
              className="typo-rl-body2 h-12 flex-1 rounded-l-lg bg-rl-secondary_kyobo-gray-original font-medium text-rl-gray-900"
              disabled={buttons[0].disabled}
              onClick={
                buttons[0].onClick
                  ? buttons[0].onClick
                  : () => {
                      closePopup()
                    }
              }
            >
              {buttons[0].title}
            </button>
            <button
              id={buttons[1].id}
              className={`typo-rl-body2 h-12 flex-1 rounded-r-lg font-medium ${"bg-rl-primary-normal text-rl-common-white"}`}
              disabled={buttons[1].disabled}
              onClick={buttons[1].onClick}
            >
              {buttons[1].title}
            </button>
          </>
        ) : (
          <></>
        )
      }
      className={`${className && className}`}
    >
      {children}
    </PopupBaseLayout>
  )
}
